import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { photoUrl, isVideo } from "@/lib/api";

export default function Hero({ content, stats, photos = [] }) {
  const hero = content?.hero || {};
  // 1) admin-selected hero image (via Textos tab)
  let heroPhoto = null;
  if (hero.image_filename) {
    heroPhoto = photos.find((p) => p.filename === hero.image_filename);
  }
  // 2) fall back to a preferred cinematic photo
  if (!heroPhoto) {
    const preferred = ["rack-vivo-01.jpg", "control-01.jpg", "control-02.jpg", "rack-vivo-02.jpg"];
    for (const f of preferred) { heroPhoto = photos.find((p) => p.filename === f); if (heroPhoto) break; }
  }
  if (!heroPhoto) heroPhoto = photos[0];
  const bgUrl = heroPhoto ? photoUrl(heroPhoto) : null;

  return (
    <section data-testid={HOME.hero} className="relative min-h-[100vh] flex flex-col">
      {/* Full-bleed photo */}
      <div className="absolute inset-0">
        {bgUrl && (isVideo(heroPhoto)
          ? <video src={bgUrl} autoPlay muted loop playsInline className="w-full h-full object-cover" style={{ filter: "saturate(0.85) contrast(1.05) brightness(0.55)" }} />
          : <img src={bgUrl} alt="" className="w-full h-full object-cover" style={{ filter: "saturate(0.85) contrast(1.05) brightness(0.55)" }} />
        )}
        {/* Dark overlays for legibility */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(6,10,20,0.92) 0%, rgba(6,10,20,0.78) 45%, rgba(6,10,20,0.55) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(6,10,20,0.4) 70%, #060A14 100%)" }} />
      </div>

      {/* Fibra óptica: luz viaja de esquina inf-izq a esquina sup-der */}
      <svg aria-hidden className="absolute inset-0 w-full h-full pointer-events-none">
        <line x1="0" y1="100%" x2="100%" y2="0%" stroke="#1E5FFF" strokeWidth="1" strokeOpacity="0.12" />
        <motion.line
          x1="0" y1="100%" x2="100%" y2="0%"
          stroke="#4d8cff"
          strokeWidth="3"
          strokeLinecap="round"
          filter="url(#fiber-glow)"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: [0, 1, 1], opacity: [0, 1, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 9, ease: "easeOut", times: [0, 0.85, 1] }}
        />
        <defs>
          <filter id="fiber-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
      </svg>

      {/* Content */}
      <div className="relative flex-1 flex items-center pt-28 pb-20 px-5 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow mb-5">
              {content?.brand?.badge || "Infraestructura IT · Broadcasting profesional"}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.05 }}
              className="font-bold text-fg leading-[0.98] tracking-tight"
              style={{ fontSize: "clamp(44px, 6.5vw, 96px)" }}
            >
              {hero.title_pre || "Tu infraestructura,"}{" "}
              <span className="text-fg">{hero.title_em || "funcionando."}</span>{" "}
              <span className="text-mute">{hero.title_post || "Siempre."}</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.18 }}
              className="mt-8 max-w-2xl text-fg-2 text-[17px] sm:text-[18px] leading-relaxed">
              {hero.subtitle}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.32 }}
              className="mt-10 flex flex-wrap gap-3">
              <a href="#contacto" data-testid={HOME.ctaPrimary} className="btn-solid inline-flex items-center gap-2 px-6 py-3.5 text-[14px] rounded-sm">
                {hero.cta_primary || "Conversemos"} <ArrowUpRight className="w-4 h-4" />
              </a>
              <a href="#servicios" data-testid={HOME.ctaSecondary} className="btn-line inline-flex items-center gap-2 px-6 py-3.5 text-[14px] rounded-sm">
                {hero.cta_secondary || "Ver servicios"}
              </a>
            </motion.div>
          </div>

          {/* Right column: 3 metric cards stacked vertically */}
          <div className="lg:col-span-4 space-y-3">
            {(stats || []).map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.4 + i*0.1 }}
                className="bg-surface/70 backdrop-blur-sm border border-line-2 p-5 rounded-sm flex items-baseline gap-4">
                <div className="text-fg font-semibold leading-none" style={{ fontSize: "clamp(32px, 3vw, 44px)" }}>{s.value}</div>
                <div className="text-mute text-[13px] leading-snug flex-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom strip: short index of the page */}
      <div className="relative border-t border-line-2 bg-base/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between gap-4 overflow-x-auto">
          <div className="flex items-center gap-6 sm:gap-8 text-mute text-[12px] tracking-wide whitespace-nowrap">
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-brand inline-block rounded-full" /> Servicios gestionados</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">Redes y seguridad</span>
            <span className="hidden md:inline">·</span>
            <span className="hidden md:inline">Cloud y servidores</span>
            <span className="hidden lg:inline">·</span>
            <span className="hidden lg:inline">Broadcasting profesional</span>
          </div>
          <a href="#servicios" className="text-mute hover:text-fg text-[12px] inline-flex items-center gap-1.5 whitespace-nowrap">
            Explorar <ArrowDown className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}
